import React, { useRef, useCallback } from 'react';
// ag-grid v35 uses package "exports"; eslint-plugin-import cannot resolve them
/* eslint-disable import/no-unresolved */
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
/* eslint-enable import/no-unresolved */

ModuleRegistry.registerModules([AllCommunityModule]);

export const AG_STYLES = `
  .ag-theme-alpine .ag-root-wrapper { border: none; }
  .ag-theme-alpine .ag-header { background-color: #f8faff !important; border-bottom: 2px solid #032C65 !important; }
  .ag-theme-alpine .ag-header-cell { font-size: 12px !important; font-weight: 700 !important; color: #032C65 !important; text-transform: uppercase; letter-spacing: 0.05em; }
  .ag-theme-alpine .ag-header-cell-label { cursor: grab; }
  .ag-theme-alpine .ag-header-cell-label:active { cursor: grabbing; }
  .ag-theme-alpine .ag-header-cell-label::before {
    content: '⠿';
    font-size: 14px;
    margin-right: 8px;
    color: #032C65;
    opacity: 0.3;
    cursor: grab;
  }
  .ag-theme-alpine .ag-header-cell-label:hover::before { opacity: 0.8; }
  .ag-theme-alpine .ag-header-cell-label[title=""]::before, 
  .ag-theme-alpine .ag-header-cell[col-id="action"] .ag-header-cell-label::before,
  .ag-theme-alpine .ag-header-cell:has(.ag-header-cell-label:empty)::before {
    display: none;
  }
  .ag-theme-alpine .ag-icon { color: #032C65 !important; }
  .ag-theme-alpine .ag-sort-indicator-icon { color: #032C65 !important; }
  .ag-theme-alpine .ag-header-cell-resize::after {
    content: '|' !important;
    background: none !important;
    color: #032C65 !important;
    opacity: 0.5 !important;
    font-size: 16px !important;
    font-weight: 100 !important;
    width: auto !important;
    height: 24px !important;
    display: flex !important;
    align-items: center !important;
    position: absolute !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    right: -6px !important;
  }
  .ag-theme-alpine .ag-header-cell-resize:hover::after {
    opacity: 1 !important;
    content: '│' !important;
    font-weight: 700 !important;
  }
  .ag-theme-alpine .ag-cell { border: none !important; display: flex; align-items: center; font-size: 14px; color: #111827; }
  .ag-theme-alpine .ag-row { border-bottom: 1px solid #f3f4f6 !important; transition: background 0.12s; }
  .ag-theme-alpine .ag-row-even { background-color: #ffffff; }
  .ag-theme-alpine .ag-row-odd { background-color: #fafbff; }
  .ag-theme-alpine .ag-row:hover { background-color: #eef2ff !important; }
  .ag-theme-alpine .ag-row-selected { background-color: #e0e7ff !important; }
  .ag-theme-alpine .ag-filter input { color: #111827 !important; background: #fff !important; border: 1px solid #d1d5db !important; border-radius: 4px !important; padding-left: 28px !important; min-height: 28px !important; box-sizing: border-box !important; }
  .ag-theme-alpine .ag-filter input::placeholder { color: #6b7280 !important; opacity: 1 !important; }
  .ag-theme-alpine .ag-filter select { color: #111827 !important; background: #fff !important; }
  .ag-theme-alpine .ag-popup .ag-filter { background: #fff; box-shadow: 0 4px 16px rgba(0,0,0,0.12); border-radius: 6px; padding: 8px; min-width: 200px; }
  .ag-theme-alpine .ag-checkbox-input-wrapper { cursor: pointer; }
`;

export const agDefaultColDef = {
  sortable: true,
  resizable: true,
};

export function getStatusStyle(status) {
  const base = {
    display: 'inline-block',
    padding: '2px 12px',
    borderRadius: '30px',
    fontWeight: 700,
    fontSize: '12px',
  };
  if (status === 'rejected') return { ...base, color: '#FF003F', backgroundColor: '#F8E3E1' };
  if (status === 'pending') return { ...base, color: '#b07d2e', backgroundColor: '#fdf3dc' };
  if (status === 'accepted') return { ...base, color: '#6ca181', backgroundColor: '#dff8e7' };
  if (status === 'completed') return { ...base, color: '#6ca181', backgroundColor: '#dff8e7' };
  if (status === 'approved') return { ...base, color: '#6d8ee9', backgroundColor: '#e0e9fa' };
  if (status === 'placed') return { ...base, color: '#d1b573', backgroundColor: '#fcf9ce' };
  return base;
}

export function StatusCell({ value, t }) {
  return <span style={getStatusStyle(value)}>{t ? t(value) : value}</span>;
}

const STORAGE_PREFIX = 'agGrid_colState_';

export default function AgGridWrapper({
  clickable,
  storageKey,
  onGridReady,
  onColumnMoved,
  onColumnResized,
  onSortChanged,
  theme = 'legacy',
  ...props
}) {
  const gridRef = useRef(null);

  const resolvedKey = storageKey || (typeof window !== 'undefined' ? window.location.pathname : null);

  const saveState = useCallback(() => {
    if (!resolvedKey || !gridRef.current?.api) return;
    const state = gridRef.current.api.getColumnState();
    localStorage.setItem(STORAGE_PREFIX + resolvedKey, JSON.stringify(state));
  }, [resolvedKey]);

  const handleGridReady = useCallback((event) => {
    if (resolvedKey) {
      const saved = localStorage.getItem(STORAGE_PREFIX + resolvedKey);
      if (saved) {
        try {
          event.api.applyColumnState({ state: JSON.parse(saved), applyOrder: true });
        } catch {
          localStorage.removeItem(STORAGE_PREFIX + resolvedKey);
        }
      }
    }
    onGridReady?.(event);
  }, [resolvedKey, onGridReady]);

  const handleColumnMoved = useCallback((event) => {
    saveState();
    onColumnMoved?.(event);
  }, [saveState, onColumnMoved]);

  const handleColumnResized = useCallback((event) => {
    if (event.finished) saveState();
    onColumnResized?.(event);
  }, [saveState, onColumnResized]);

  const handleSortChanged = useCallback((event) => {
    saveState();
    onSortChanged?.(event);
  }, [saveState, onSortChanged]);

  return (
    <div className="ag-theme-alpine" style={{ width: '100%' }}>
      <style>{AG_STYLES}</style>
      {clickable && <style>{`.ag-theme-alpine .ag-row { cursor: pointer; }`}</style>}
      <AgGridReact
        ref={gridRef}
        domLayout="autoHeight"
        suppressCellFocus
        suppressDragLeaveHidesColumns
        rowHeight={52}
        headerHeight={52}
        onGridReady={handleGridReady}
        onColumnMoved={handleColumnMoved}
        onColumnResized={handleColumnResized}
        onSortChanged={handleSortChanged}
        {...props}
        // Keep CSS file themes (ag-grid.css / alpine). Pass theme to opt into Theming API later.
        theme={theme}
      />
    </div>
  );
}
